import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@up2stay/up2promo-ui';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { LegalLinks } from '@/core/components/auth/legal-links';
import { BackButton } from '@/core/components/ui/back-button';

/** Profiles SoT page title for auth entry (`--t-h1`). */
export const AUTH_TITLE_CLASS =
  'text-[26px] font-bold leading-[1.1] tracking-tight text-foreground md:text-[22px]';

/** Profiles SoT body for auth descriptions (`--t-body`). */
export const AUTH_DESCRIPTION_CLASS =
  'text-[13.5px] leading-relaxed text-muted-foreground';

/** Profiles SoT field label (`--t-label`). */
export const AUTH_FIELD_LABEL_CLASS =
  'text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground';

interface AuthLayoutWrapperProps {
  children: ReactNode;
  title?: string;
  description?: string;
  backTo?: string;
}

/**
 * Single children tree — a mobile + desktop duplicate made Enter submit the
 * hidden 0×0 form (DEV-285).
 *
 * Centered Light Studio card on md+; full-bleed canvas on mobile.
 */
export function AuthLayoutWrapper({
  children,
  title,
  description,
  backTo,
}: AuthLayoutWrapperProps) {
  const location = useLocation();
  const showBackOnDesktop = backTo && location.pathname !== '/auth';

  return (
    <div className="flex h-full w-full flex-col overflow-hidden px-4 sm:px-6">
      <div className="flex flex-1 flex-col py-8 md:items-center md:justify-center">
        {backTo ? (
          <div className="mb-4 md:hidden">
            <BackButton to={backTo} />
          </div>
        ) : null}
        <Card className="w-full border-0 bg-transparent shadow-none md:max-w-md md:border md:bg-card md:shadow-[var(--shadow-card)]">
          {showBackOnDesktop ? (
            <div className="hidden px-6 pt-6 md:block">
              <BackButton to={backTo} />
            </div>
          ) : null}
          {title ? (
            <CardHeader className="gap-2 px-0 md:px-6 md:pt-6">
              <CardTitle className={AUTH_TITLE_CLASS}>{title}</CardTitle>
              {description ? (
                <CardDescription className={AUTH_DESCRIPTION_CLASS}>
                  {description}
                </CardDescription>
              ) : null}
            </CardHeader>
          ) : null}
          <CardContent className="flex flex-col gap-5 px-0 md:px-6 md:pb-6">
            {children}
            <LegalLinks />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
